// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — Profitability v2 settings (owner, 2026-10-14):
//   • Split percentages — the S·M·L·E reserve % and Blue Dev's share of net profit (Owl gets the rest), changed
//     from a chosen month on with a reason. Past months keep the split they had; every change stays on record.
//   • Automatic month close — on/off. Starts OFF so past months can be back-filled before they freeze.
// Every change is audit-logged server-side.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import AppButton from '../../../components/ui/AppButton'
import LoadState from '../../../components/ui/LoadState'
import Notice from '../../../components/ui/Notice'
import Section from '../../../components/ui/Section'
import { useToast } from '../../../components/ui/Toast'
import { AppTextField, SelectField, ToggleSwitch } from '../../../components/ui/FormFields'
import { DataGrid, type DataGridColumn } from '../../../components/DataGrid'
import { friendlyError } from '../../../api/errors'
import { financeKeys, getFinanceSplit, setAutoClose, setFinanceSplit, type FinanceSplitInfo } from '../../../api/finance'
import { monthLabel } from './CostItemDialogs'

type Change = FinanceSplitInfo['changes'][number]

const pct = (s: string) => (s.trim() === '' ? NaN : Number(s))
const pctOk = (n: number, max: number) => Number.isFinite(n) && n >= 0 && n <= max

function splitMonths(): string[] {
  const out: string[] = []
  const now = new Date()
  for (let i = 12; i >= -12; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1))
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  return out
}

const historyCols: DataGridColumn<Change>[] = [
  { key: 'when', header: 'Changed', minWidth: 150, value: c => c.changed_at, render: c => new Date(c.changed_at).toLocaleString() },
  { key: 'by', header: 'By', value: c => c.changed_by },
  { key: 'from', header: 'Applies from', value: c => c.effective_from, render: c => c.source === 'existing' ? 'the beginning' : monthLabel(c.effective_from) },
  { key: 'reserve', header: 'Reserve', align: 'right', value: c => c.reserve_pct, render: c => `${c.reserve_pct}%` },
  { key: 'bucket', header: 'Bucket', align: 'right', value: c => c.bucket_pct ?? -1, render: c => (c.bucket_pct === null ? 'old model' : `${c.bucket_pct}%`) },
  { key: 'split', header: 'Blue Dev / Owl', align: 'right', value: c => c.dev_split_pct, render: c => <b>{c.dev_split_pct}% / {c.owl_pct}%</b> },
  { key: 'why', header: 'Why', minWidth: 200, value: c => c.reason },
]

export default function SettingsPanel() {
  const qc = useQueryClient()
  const toast = useToast()
  const months = useMemo(splitMonths, [])
  const q = useQuery({ queryKey: financeKeys.split, queryFn: getFinanceSplit })
  const [reserve, setReserve] = useState('')
  const [dev, setDev] = useState('')
  const [bucket, setBucket] = useState('')
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7))
  const [reason, setReason] = useState('')

  const saveSplit = useMutation({
    mutationFn: () => setFinanceSplit(pct(reserve), pct(dev), month, reason.trim(), bucket.trim() === '' ? null : pct(bucket)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financeKeys.all })
      toast.show(`Split from ${monthLabel(month)}: ${bucket.trim() ? `bucket ${pct(bucket)}%, ` : ''}S·M·L·E ${pct(reserve)}%, Blue Dev ${pct(dev)}% / Owl ${100 - pct(dev)}%`, 'success')
      setReserve(''); setDev(''); setBucket(''); setReason('')
    },
  })
  const toggle = useMutation({
    mutationFn: (on: boolean) => setAutoClose(on),
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: financeKeys.all })
      toast.show(r.auto_close_enabled ? 'Automatic month close is ON' : 'Automatic month close is paused', 'success')
    },
    onError: e => toast.show(friendlyError(e), 'error'),
  })

  const r = pct(reserve), d = pct(dev), b = pct(bucket)
  const bucketOk = bucket.trim() === '' || (pctOk(b, 99.99) && (!Number.isFinite(r) || b >= r))
  const valid = pctOk(r, 99.99) && pctOk(d, 100) && bucketOk && reason.trim().length >= 5

  return (
    <LoadState query={q} what="the finance settings" isEmpty={() => false} empty={null}>
      {data => (
        <>
          <Section title="Profit split"
            subtitle={<>With an <b>operating bucket</b> (e.g. 40%): running costs are paid from it first, S·M·L·E gets what is left up to its %,
              anything still left is held, and the partners split the rest straight away. Without one (old model): the reserve and every expense come
              off, then the rest is split. A change applies from the month you pick — earlier months keep what they had.</>}>
            <Box sx={{ fontSize: 13.5, mb: 1.5 }}>
              Now: {data.current.bucket_pct !== null
                ? <>bucket <b>{data.current.bucket_pct}%</b> (S·M·L·E <b>{data.current.reserve_pct}%</b> + running-cost target <b>{data.current.bucket_pct - data.current.reserve_pct}%</b>)</>
                : <>reserve <b>{data.current.reserve_pct}%</b> (old model, no bucket)</>}
              {' '}· Blue Dev <b>{data.current.dev_split_pct}%</b> / Owl <b>{data.current.owl_pct}%</b>
            </Box>
            {saveSplit.isError && <Notice severity="error">{friendlyError(saveSplit.error)}</Notice>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
              <AppTextField label="Operating bucket %" value={bucket} onChange={e => setBucket(e.target.value)}
                placeholder={data.current.bucket_pct !== null ? String(data.current.bucket_pct) : 'empty = old model'}
                inputProps={{ inputMode: 'decimal' }} error={!bucketOk}
                helperText={!bucketOk ? 'At least the S·M·L·E %' : bucket.trim() && pctOk(r, 99.99) && pctOk(b, 99.99) ? `running-cost target ${Math.round((b - r) * 100) / 100}%` : ' '} />
              <AppTextField label="S·M·L·E reserve %" value={reserve} onChange={e => setReserve(e.target.value)} placeholder={String(data.current.reserve_pct)}
                inputProps={{ inputMode: 'decimal' }} error={reserve !== '' && !pctOk(r, 99.99)} />
              <AppTextField label="Blue Dev % of net profit" value={dev} onChange={e => setDev(e.target.value)} placeholder={String(data.current.dev_split_pct)}
                inputProps={{ inputMode: 'decimal' }} error={dev !== '' && !pctOk(d, 100)}
                helperText={pctOk(d, 100) ? `Owl gets ${100 - d}%` : ' '} />
              <SelectField label="Starts" value={month} onChange={setMonth} options={months.map(m => ({ value: m, label: monthLabel(m) }))} />
            </Box>
            <AppTextField label="Why (kept in the history)" fullWidth value={reason} onChange={e => setReason(e.target.value)}
              placeholder="e.g. new agreement with Blue Dev signed 10/14" inputProps={{ maxLength: 400 }} />
            <Box sx={{ mt: 1.5, mb: 2 }}>
              <AppButton variant="contained" disabled={!valid || saveSplit.isPending} onClick={() => { saveSplit.reset(); saveSplit.mutate() }}>
                {saveSplit.isPending ? 'Saving…' : 'Save new split'}
              </AppButton>
            </Box>
            <Box sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>History — every change, newest first</Box>
            <DataGrid rows={data.changes} columns={historyCols} storageKey="finance-v2-split-history" getRowKey={c => c.id} dense />
          </Section>

          <Section title="Automatic month close"
            subtitle={`When on, each month freezes on day ${data.auto_close_day} of the next month (after Azure’s late charges land). Keep it paused while you enter past prices, income and costs; you can still close or reopen a month by hand in Data sources & close.`}>
            <ToggleSwitch checked={data.auto_close_enabled} disabled={toggle.isPending}
              label={data.auto_close_enabled ? 'On — months close automatically' : 'Paused — nothing closes automatically'}
              onChange={on => toggle.mutate(on)} />
          </Section>
        </>
      )}
    </LoadState>
  )
}
